import {
  getIncomeTypeLabel,
  INCOME_PARENT_LABEL,
  isIncomeSelectionValue,
  selectionValueToIncomeType,
} from '../utils/incomeClassification';
import { API_URL } from '../config/constants';
import { publishFinanceTrendDataChange } from '../utils/trendDataInvalidation';
import { createMutationContractError } from '../utils/mutationContracts';

export const todayISO = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

export const createEmptyManualTransactionForm = () => ({
  merchant: '',
  amount: '',
  date: todayISO(),
  categoryId: '',
  notes: '',
});

export const validateManualTransactionForm = (form) => {
  const errors = {};
  if (!form.merchant.trim()) errors.merchant = 'Merchant is required';
  const amount = parseFloat(form.amount);
  if (!form.amount || !Number.isFinite(amount) || amount === 0) {
    errors.amount = 'Enter a non-zero value';
  }
  if (!form.date || !/^\d{4}-\d{2}-\d{2}$/.test(form.date)) {
    errors.date = 'Enter a valid date';
  }
  return errors;
};

export const isManualTransactionFormPristine = (form) => (
  !form.merchant
  && !form.amount
  && !form.notes
  && !form.categoryId
  && form.date === todayISO()
);

export const buildCreatedManualTransaction = ({ responseBody, form, categories = [] }) => {
  const incomeType = selectionValueToIncomeType(form.categoryId);
  const categoryId = form.categoryId && !isIncomeSelectionValue(form.categoryId) ? Number(form.categoryId) : null;
  const category = categoryId != null
    ? categories.find((item) => Number(item.id) === categoryId)
    : null;
  const parentCategory = category?.parent_id != null
    ? categories.find((item) => Number(item.id) === Number(category.parent_id))
    : null;
  const rawAmountValue = parseFloat(form.amount);
  const amountValue = incomeType ? -Math.abs(rawAmountValue) : Math.abs(rawAmountValue);

  return {
    id: responseBody.id,
    date: form.date,
    merchant: form.merchant.trim(),
    amount: amountValue,
    account: null,
    accountLabel: null,
    categoryId,
    categoryName: incomeType ? getIncomeTypeLabel(incomeType, categories) : (category?.name || null),
    parentCategoryName: incomeType ? INCOME_PARENT_LABEL : (parentCategory?.name || null),
    autoCateg: false,
    isPending: false,
    source: 'MANUAL',
    notes: form.notes.trim() || '',
    merchantLogoUrl: null,
    bankLogoUrl: null,
    isIncome: Boolean(incomeType),
    incomeType,
    incomeSource: incomeType ? form.merchant.trim() : null,
    incomeSplits: [],
    version: Number.isInteger(Number(responseBody.version)) ? Number(responseBody.version) : 0,
  };
};

export const createManualTransaction = async ({
  authenticatedFetch,
  householdId,
  form,
  categories = [],
  idempotencyKey = null,
}) => {
  const incomeType = selectionValueToIncomeType(form.categoryId);
  const normalizedAmount = incomeType
    ? -Math.abs(parseFloat(form.amount))
    : Math.abs(parseFloat(form.amount));

  const response = await authenticatedFetch(
    `${API_URL}/transactions/${householdId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      },
      body: JSON.stringify({
        isIncome: Boolean(incomeType),
        incomeType,
        merchant: form.merchant.trim(),
        amount: normalizedAmount,
        date: form.date,
        categoryId: isIncomeSelectionValue(form.categoryId) ? null : (form.categoryId || null),
        notes: form.notes.trim() || null,
      }),
    }
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw createMutationContractError({
      response,
      data: body,
      fallbackMessage: 'Failed to create transaction',
    });
  }

  const body = await response.json().catch(() => null);
  const id = body?.id;
  const hasAuthoritativeId = (typeof id === 'number' && Number.isSafeInteger(id) && id > 0)
    || (typeof id === 'string' && /^[1-9]\d*$/.test(id) && Number.isSafeInteger(Number(id)));
  if (!body || typeof body !== 'object' || Array.isArray(body) || !hasAuthoritativeId) {
    // The server may have committed the mutation. Keep this outcome ambiguous
    // so an explicit retry reuses the existing intent instead of creating one.
    const error = new Error('Failed to create transaction');
    error.code = 'INVALID_RESPONSE';
    error.retryable = true;
    throw error;
  }
  publishFinanceTrendDataChange({ householdId });
  return buildCreatedManualTransaction({ responseBody: body, form, categories });
};
